import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { OnboardingService, OnboardingStatus } from './onboarding.service';
import { LoggingService } from './logging.service';

/**
 * UserStateService
 *
 * Manages user onboarding state throughout the session.
 * This is APPLICATION STATE, not a cache.
 *
 * Key principles:
 * 1. Fetch once on login
 * 2. Update state as user progresses through onboarding
 * 3. No expiration - state lives until logout or page refresh
 * 4. Single source of truth for all components
 * 5. No redundant API calls once onboarding complete
 *
 * Storage strategy:
 * - Memory-only (signal-based)
 * - Cleared on page refresh (fresh state on reload)
 * - Cleared on logout (explicit cleanup)
 * - No persistence (no offline support needed)
 */
@Injectable({
  providedIn: 'root'
})
export class UserStateService {
  private onboardingService = inject(OnboardingService);
  private logger = inject(LoggingService);

  // Private writable signal for onboarding state
  private _onboardingState = signal<OnboardingStatus | null>(null);

  // Public readonly signal - components can read but not write
  readonly onboardingState = this._onboardingState.asReadonly();

  // Computed signal: Is onboarding complete?
  // All four steps must be satisfied before a user can access the app:
  //   1. Email verified
  //   2. Subscription chosen (accountStatus !== 'new')
  //   3. Terms accepted
  //   4. Profile complete (first and last name set)
  readonly isOnboardingComplete = computed(() => {
    const state = this._onboardingState();
    if (!state) return false;

    return state.emailVerified
      && state.accountStatus !== 'new'
      && state.termsAccepted
      && state.profileComplete;
  });

  // Computed signal: Current account status (defaults to 'new' until state is loaded)
  public readonly accountStatus = computed(() => this._onboardingState()?.accountStatus ?? 'new');

  // Computed signal: Trial end date
  public readonly trialEndsAt = computed(() => this._onboardingState()?.trialEndsAt);

  /**
   * Is user currently in a trial period?
   *
   * Implemented as a method instead of a computed signal because it depends on
   * the current time (new Date()), which is not reactive. This ensures the value
   * is evaluated fresh each time it is called.
   */
  public isTrialing(): boolean {
    const state = this._onboardingState();
    return !!state?.trialEndsAt && new Date(state.trialEndsAt) > new Date();
  }

  /**
   * Initialize state by fetching from API
   * Called once after login
   */
  initializeState(): Observable<OnboardingStatus> {
    this.logger.info('[UserStateService] Initializing state from API');

    return this.onboardingService.getOnboardingStatus().pipe(
      tap(status => {
        this.logger.debug('[UserStateService] State initialized:', status);
        this._onboardingState.set(status);
      })
    );
  }

  /**
   * Get state if it exists, otherwise fetch from API
   * This is the primary method components should use
   */
  getOrFetchState(): Observable<OnboardingStatus> {
    const current = this._onboardingState();

    if (current) {
      this.logger.debug('[UserStateService] Returning existing state (no API call)');
      return of(current);
    }

    this.logger.info('[UserStateService] State not initialized, fetching from API');
    return this.initializeState();
  }

  /**
   * Update specific fields in state without API call
   * Called after mutations (accept terms, complete profile, verify email)
   */
  updateState(partial: Partial<OnboardingStatus>): void {
    const current = this._onboardingState();

    if (!current) {
      this.logger.warn('[UserStateService] Cannot update null state, initializing first');
      // If state doesn't exist, we can't update it
      // This shouldn't happen in normal flow, but handle gracefully
      return;
    }

    const updated = { ...current, ...partial };
    this.logger.debug('[UserStateService] State updated:', partial);
    this._onboardingState.set(updated);
  }

  /**
   * Clear state on logout
   */
  clearState(): void {
    this.logger.info('[UserStateService] State cleared');
    this._onboardingState.set(null);
  }

  /**
   * Force refresh state from API
   * Use this in specific scenarios like after Stripe redirect
   */
  refreshState(): Observable<OnboardingStatus> {
    this.logger.info('[UserStateService] Force refreshing state from API');
    return this.initializeState();
  }
}
